import {
    Button,
    Card,
    Checkbox,
    Select,
    Spinner,
    TextArea,
    TextField,
    Toggle,
    Tooltip,
} from '@cimpress/react-components';
import { useContext, useEffect, useState } from 'react';
import { SketchPicker } from 'react-color';
import { getInspirationGenerateUrls, getPhotoSceneUrl } from '../api';
import { ApiKeyContext } from '../App';
import {
    CardBuilder,
    Content,
    Control,
    Page,
    PreviewRow,
    PreviewRows,
    PreviewSection,
    SurfaceSelector,
    type SelectorOption,
} from '../Components';
import { InputJsonSelector } from '../Components/common/InputJsonSelector';
import { ImageAssetControl } from '../Components/InspirationGenerate/ImageAssetControl';
import { useInspirationGenerateParams } from '../Components/InspirationGenerate/useInspirationGenerateParams';
import { NONE_SELECTOR_OPTION } from '../constants';
import contentAlignmentOptions from '../data/contentAlignmentOptions.json';
import presetSurfaces from '../data/generateSurfaceJson.json';
import presetSurfaceUrls from '../data/generateSurfaceUrls.json';
import predefinedLayoutFilterOptions from '../data/InspirationGenerateLayoutFilterOptions.json';
import { isUrl, useCancelableEffect } from '../tools'; // Removed useDebounce import
import { isSmallScreen } from '../tools/isSmallScreen';
import { brandProfileUrls } from '../data/generateBrandProfileDocuments';
import { CustomSelector } from '../Components/common/CustomSelector';

const colorPaletteOptions = [
    NONE_SELECTOR_OPTION,
    {
        label: 'Custom',
        value: `{
            "primary": "#000000",
            "secondary": "#333333",
            "neutral": "#666666",
            "accent": "#000000",
            "background": "#FFFFFF"
        }`,
    },
    {
        label: 'Color Palette 1',
        value: `{
            "primary": "#26c799",
            "secondary": "#ffaa00"
        }`,
    },
    {
        label: 'Color Palette 2',
        value: `{
            "primary": "#7ED321",
            "secondary": "#F5A623"
        }`,
    },
    {
        label: 'Color Palette 3',
        value: `{
            "primary": "#D0021B",
            "secondary": "#000000"
        }`,
    },
];

export const InspirationGeneratePage = () => {
    const {
        primaryText,
        secondaryText,
        imageAsset,
        substrateColor,
        textOnly,
        surfaceSpecificationUrl,
        surfaceSpecification,
        themeQuery,
        colorPaletteSelectedOption,
        iconCollection,
        contentAlignment: contentAlignment,
        isVersionV4,
        filters,
        setColorPaletteSelectedOption,
        setSurfaceSpecificationUrl,
        setSurfaceSpecification,
        setThemeQuery,
        setTextOnly,
        setSubstrateColor,
        setPrimaryText,
        setSecondaryText,
        setImageAsset,
        setContentAlignment: setContentAlignment,
        setIsVersionV4,
        setFilters,
        brandProfileUrl,
        setBrandProfileUrl,
    } = useInspirationGenerateParams();
    const [resultRow, setResultRow] = useState(PreviewRow());
    const [isLoading, setIsLoading] = useState(false);
    const [callGenerate, setCallGenerate] = useState(false);
    const [customColorPaletteJson, setCustomColorPaletteJson] = useState(JSON.parse(colorPaletteOptions[1].value));
    const apiKey = useContext(ApiKeyContext);

    useEffect(() => {
        setCallGenerate(true);
        if (!filters) {
            setFilters(predefinedLayoutFilterOptions[1].value);
        }
    }, [setFilters, filters]);

    useCancelableEffect(
        (signal) => {
            (async () => {
                if (
                    (surfaceSpecificationUrl || surfaceSpecification) &&
                    (primaryText || secondaryText || !textOnly) &&
                    callGenerate
                ) {
                    setIsLoading(true);

                    try {
                        const texts = primaryText
                            ? [
                                  {
                                      content: primaryText,
                                      type: 'primary',
                                      isPlaceholder: false,
                                  },
                              ]
                            : [];

                        if (secondaryText)
                            texts.push({ content: secondaryText, type: 'secondary', isPlaceholder: false });

                        const colorPalette =
                            colorPaletteSelectedOption?.value && colorPaletteSelectedOption.value.trim() !== ''
                                ? colorPaletteSelectedOption.label === 'Custom'
                                    ? customColorPaletteJson
                                    : JSON.parse(colorPaletteSelectedOption.value)
                                : null;

                        const resultDocumentUrls = await getInspirationGenerateUrls({
                            themeQuery,
                            iconCollection,
                            texts,
                            imageAsset: !textOnly && imageAsset ? imageAsset : '',
                            surfaceSpecificationUrl,
                            surfaceSpecification,
                            substrateColor,
                            useImage: !textOnly,
                            colorPalette,
                            contentAlignment: contentAlignment.value,
                            signal,
                            apiKey,
                            version: isVersionV4 ? 'v4' : 'v3',
                            filters,
                            brandProfileUrl: brandProfileUrl ?? undefined,
                        });

                        const photoSceneUrl = surfaceSpecificationUrl
                            ? ((await getPhotoSceneUrl(surfaceSpecificationUrl)) ?? '')
                            : undefined;

                        const resultCards = resultDocumentUrls?.map((documentUrl, i) =>
                            CardBuilder.ofType('documentUrl')
                                .withTitle(`Result Document ${i + 1}`)
                                .withData({
                                    documentUrl,
                                    surfaceUrl: surfaceSpecificationUrl ?? undefined,
                                    photoSceneUrl,
                                    backgroundColor: substrateColor?.substring(1) ?? 'FFFFFF',
                                })
                                .build(),
                        );

                        setResultRow(PreviewRow(...resultCards));
                    } catch (error) {
                        if (signal.aborted) {
                            return;
                        }

                        const resultCard = CardBuilder.ofType('documentUrl')
                            .withTitle('Result Document')
                            .withLoader(async () => {
                                throw error;
                            })
                            .build();
                        setResultRow(PreviewRow(resultCard));
                    } finally {
                        setIsLoading(false);
                        setCallGenerate(false);
                    }
                }
            })();
        },
        [
            themeQuery,
            primaryText,
            secondaryText,
            imageAsset,
            surfaceSpecificationUrl,
            substrateColor,
            colorPaletteSelectedOption,
            customColorPaletteJson,
            textOnly,
            iconCollection,
            surfaceSpecification,
            contentAlignment,
            apiKey,
            callGenerate,
            filters,
            isVersionV4,
            brandProfileUrl,
        ],
    );

    const onColorPaletteChange = (selected: SelectorOption | null | undefined) => {
        if (!selected) {
            return;
        }
        setColorPaletteSelectedOption(selected);
    };

    function onClickGenerate() {
        setCallGenerate(true);
    }

    return (
        <Page>
            <Page.Title>Generate</Page.Title>
            <Page.Controls>
                <Control>
                    <Button onClick={onClickGenerate}>Generate</Button>
                    <div style={{ alignItems: 'center' }}>
                        <Toggle on={isVersionV4} onClick={() => setIsVersionV4(!isVersionV4)} size="sm" />
                        <span style={{ marginLeft: '10px' }}>Use V4</span>
                    </div>
                    <Control.Title>Theme Query</Control.Title>
                    <TextField
                        label="Theme Query"
                        onChange={(e) => setThemeQuery(e.target.value)}
                        value={themeQuery ?? ''}
                    />
                </Control>

                <Control spacing="compact">
                    <Control.Title>Texts</Control.Title>

                    <TextField
                        label="Primary Text"
                        onChange={(event) => setPrimaryText(event.target.value)}
                        value={primaryText ?? ''}
                    />

                    <TextField
                        label="Secondary Text"
                        onChange={(event) => setSecondaryText(event.target.value)}
                        value={secondaryText ?? ''}
                    />

                    <Card>
                        <Checkbox
                            label={
                                <Tooltip
                                    direction={isSmallScreen() ? 'bottom' : 'right'}
                                    contents="When checked, result documents will contain only text and shapes, but no images"
                                >
                                    <div>Text Only</div>
                                </Tooltip>
                            }
                            checked={textOnly}
                            onChange={(event) => {
                                setTextOnly(event.target.checked);
                                setCallGenerate(true);
                            }}
                        />
                    </Card>
                </Control>

                {isVersionV4 && (
                    <Control>
                        <Control.Title>Brand Profile URL</Control.Title>
                        <CustomSelector
                            selectorLabel="Brand Profile URL"
                            textfieldLabel="Brand Profile URL"
                            data={brandProfileUrls}
                            value={brandProfileUrl ?? ''}
                            setValue={setBrandProfileUrl}
                            validator={isUrl}
                        />
                    </Control>
                )}

                {isVersionV4 && (
                    <Control>
                        <Control.Title>Dynamic Line Filters</Control.Title>
                        <InputJsonSelector
                            rows={15}
                            label="Filters"
                            initialValue={filters}
                            onChange={setFilters}
                            predefinedValues={predefinedLayoutFilterOptions}
                        />
                    </Control>
                )}

                <ImageAssetControl
                    initialImageAsset={imageAsset ? JSON.parse(imageAsset) : null}
                    setImageAsset={setImageAsset}
                />

                <Control>
                    <Control.Title>Color Palette</Control.Title>
                    <Select
                        onChange={onColorPaletteChange}
                        value={colorPaletteSelectedOption}
                        options={colorPaletteOptions}
                        label="Color Palette"
                    />

                    {colorPaletteSelectedOption?.label !== 'None' && (
                        <div>
                            <TextArea
                                style={{ whiteSpace: 'pre-line' }}
                                value={
                                    colorPaletteSelectedOption?.label === 'Custom'
                                        ? JSON.stringify(customColorPaletteJson, null, 4)
                                        : JSON.stringify(JSON.parse(colorPaletteSelectedOption?.value), null, 4)
                                }
                                rows={7}
                                onChange={(e) => {
                                    if (colorPaletteSelectedOption?.label === 'Custom') {
                                        try {
                                            setCustomColorPaletteJson(JSON.parse(e.target.value));
                                        } catch {}
                                    }
                                }}
                            />
                        </div>
                    )}
                </Control>

                <Control>
                    <Control.Title>Surface Selection</Control.Title>
                    <SurfaceSelector
                        surfaceSpecificationUrl={surfaceSpecificationUrl ?? ''}
                        setSurfaceSpecificationUrl={setSurfaceSpecificationUrl}
                        surfaceSpecification={surfaceSpecification}
                        setSurfaceSpecification={setSurfaceSpecification}
                        presetSurfaceUrlsFromPage={presetSurfaceUrls}
                        presetSurfaceJsonFromPage={presetSurfaces}
                    />
                </Control>
                {!isVersionV4 && (
                    <Control>
                        <Control.Title>Content Alignment</Control.Title>
                        <Select
                            onChange={setContentAlignment}
                            value={contentAlignment}
                            options={contentAlignmentOptions}
                            label="Content Alignment (Vertical)"
                            isSearchable={false}
                        />
                    </Control>
                )}

                <Control>
                    <Control.Title>Substrate Color</Control.Title>
                    <SketchPicker
                        width="65%"
                        color={substrateColor ?? '#FFFFFF'}
                        onChangeComplete={(e) => setSubstrateColor(e.hex)}
                    />
                </Control>
            </Page.Controls>

            <Page.Content>
                {isLoading ? (
                    <Content.Card sticky size="large">
                        <Content.Section>
                            <Spinner />
                        </Content.Section>
                    </Content.Card>
                ) : (
                    <PreviewSection
                        introduction={
                            'This is a page to test the inspiration generate endpoint. Fill in the assets and surface URL to see generated results.'
                        }
                        showIntro={
                            !(
                                (surfaceSpecificationUrl || surfaceSpecification) &&
                                (primaryText || secondaryText || !textOnly)
                            )
                        }
                        rows={PreviewRows(resultRow)}
                    />
                )}
            </Page.Content>
        </Page>
    );
};
